import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, fade, fill, place, playerColor,
} from '../fx/common';
import {
  PLATE, duelistName, duelistPlate, metalText, putIn, tintedSkew,
} from '../fx/duelistPlate';

/**
 * **デュエルの開始（VS）**（Issue #646 v2。指示書 `../direction/start.md`。キービジュアルは無い＝方向は人が決めた）。
 *
 * ```
 * 0      カメラは盤の外（ページのカメラから見上げて、盤の奥の上空を見ている）。舞台は少し暗い（層の暗幕。HUD は落とさない）
 * 250    左から P1、右から P2 の斜めに切った鋼の板が加速して中央へ（後ろに硬い速度線）
 * 650    斜めの切り口が噛み合って急停止 → 合わせ目の閃光 2 フレーム・火花・破片 → ヒットストップ 100ms → 揺れ
 * 760    合わせ目の上に「VS」を 2.5 倍から叩きつけ → 小さく揺れて止まる（板は数 px で押し合う）
 * 1400   VS が細く潰れ、横長の帯「DUEL START」を叩きつけ。板は上下に裂けて画面の外へ
 * 1780   カメラが少し溜めてから一気に盤へ降りる（2300 で素のショットにぴったり着地 → reset）。着地で重い揺れと硬い線の輪
 * 2300   HUD が組み上がる（v2）: 顔の枠が角から叩き込まれる → 2400 ゲージの空の枠が伸びる → 2550〜3450 LP が 0 → 開始 LP へ満ちる
 *        （ゲージも数字も同じ値）・2550〜2850 紋章が回りながら落ちて噛み合う・2650〜2950 名前と手札が下りる（それまで HUD は出さない）
 * 3500   先攻の側から先攻の色の札「先攻 名前」を叩きつけ（LP が満ち終わってから）
 * 3950   札が縦に細く潰れて横へ抜ける。何も残さない
 * ```
 *
 * **カメラは素のショット（ページのカメラ）からの相対で動かす**（`scene.camera` の `tilt` / `rise` / `push`。
 * `editorPorts/fxScenePicture.js::moveShot`）。climax の既定のカメラは 90° 回転・斜めなので、指示書の「`LIVE_SHOT` に着地」は
 * 「ページのカメラに着地」と読み替えた（`LIVE_SHOT` に着地させると、`reset` で既定のカメラへ 1 フレームで跳ぶ）。
 *
 * 板（デッキのイラスト・アイコン・名前・デッキ名）は `../fx/duelistPlate.js`（win の勝者の名乗りと共有。Issue #604）。
 */
const D = 4200;
const SLIDE = 250;
const HIT = 650;
const STOP = 100;           // ヒットストップ
const VS_AT = 760;
const START_AT = 1400;
const START_SLAM = 1440;
const PLATES_OUT = 1700;
const DESCEND = 1780;       // 溜めのあと降り始める
const SWOOP = 2080;         // ここまで加速（盤はまだ画面の下の外）→ 着地へ減速しながら盤が下からせり上がる
const LAND = 2300;
// --- HUD の登場（v2。着地からの ms。`hud` の `appear` / `life`）
const H_PLATE = [LAND, LAND + 150];          // 顔の枠が角から叩き込まれる
const H_BAR = [LAND + 100, LAND + 250];      // ゲージの空の枠が外側から伸びる
const H_FILL = [LAND + 250, LAND + 1150];    // LP が 0 → 開始 LP（ゲージも数字も同じ値）
const H_BADGE = [LAND + 250, LAND + 550];    // 紋章が回りながら落ちて噛み合う
const H_DROP = [LAND + 350, LAND + 650];     // 名前・手札が下りる
const TAG_IN = H_FILL[1] + 50;               // 先攻の札は LP が満ち終わってから
const TAG_HIT = TAG_IN + 80;
const TAG_OUT = 3950;
const TAG_GONE = 4050;

const { W: PW, H: PH, SKEW } = PLATE;
const SPARKS = 30;
const SHARDS = 18;
const LINES = 14;
const RING_BARS = 40;

/** ヒットストップの時間の写し（ぶつかった後は STOP だけ遅れる） */
const hs = (t) => (t < HIT ? t : (t < HIT + STOP ? HIT : t - STOP));

export default defineEffect({
  situation: 'start',
  role: 'effect',
  label: 'デュエルの開始（VS → DUEL START → カメラが盤へ降りる → 先攻）',
  color: '#c9a44c',
  duration: () => D,
  scene: [
    'reset',
    {
      ms: 0,
      // 素のショット（ページのカメラ）から見上げた所 → 2300 で 0 に戻ってぴったり着地（power2_in で加速して止まる）
      camera: {
        tilt: { keys: [{ ms: 0, value: 78 }, { ms: SLIDE, value: 78.6 }, { ms: PLATES_OUT, value: 80 }, { ms: DESCEND, value: 83, ease: 'power2_in' }, { ms: SWOOP, value: 46, ease: 'ease_out' }, { ms: LAND, value: 0 }] },
        rise: { keys: [{ ms: 0, value: 420 }, { ms: DESCEND, value: 480, ease: 'power2_in' }, { ms: SWOOP, value: 200, ease: 'ease_out' }, { ms: LAND, value: 0 }] },
        push: { keys: [{ ms: 0, value: 320 }, { ms: DESCEND, value: 360, ease: 'power2_in' }, { ms: SWOOP, value: 150, ease: 'ease_out' }, { ms: LAND, value: 0 }] },
      },
      // 止めの間は硬く、色収差は叩きつけの瞬間だけ（舞台を落とすのは層の暗幕。grade の明るさは HUD まで落とすので使わない）
      grade: {
        contrast: { keys: [{ ms: 0, value: 1, ease: 'hold' }, { ms: HIT, value: 1.4, ease: 'hold' }, { ms: HIT + STOP, value: 1 }] },
        chroma: { keys: [{ ms: 0, value: 0, ease: 'hold' }, { ms: HIT, value: 0.012, ease: 'ease_out' }, { ms: HIT + STOP + 200, value: 0 }] },
      },
    },
    { ms: HIT + STOP, shake: { amp: 40, freq: 22, decayMs: 480 } },
    { ms: VS_AT + 30, shake: { amp: 18, freq: 24, decayMs: 300 } },
    { ms: START_SLAM, shake: { amp: 22, freq: 24, decayMs: 300 } },
    // 着地: カメラは素のショットに戻っている（tilt / rise / push が 0）ので、ここで戻しても跳ばない
    { ms: LAND, reset: true },
    { ms: LAND, shake: { amp: 60, freq: 20, decayMs: 560 } },
    { ms: TAG_HIT, shake: { amp: 10, freq: 26, decayMs: 220 } },
  ],
  sounds: () => [
    { at: SLIDE, name: 'phase_change', gain: 0.6 },
    { at: HIT - 15, name: 'attack', gain: 0.95 },
    { at: VS_AT, name: 'attack_decl', gain: 0.8 },
    { at: START_SLAM - 15, name: 'turn_change', gain: 0.8 },
    { at: DESCEND, name: 'phase_change', gain: 0.5 },
    { at: LAND - 15, name: 'attack', gain: 0.9 },
    { at: H_PLATE[1] - 15, name: 'set_card', gain: 0.9 },
    { at: H_FILL[0], name: 'life_up', gain: 0.8 },
    { at: H_BADGE[1] - 15, name: 'turn_change', gain: 0.6 },
    { at: H_FILL[1] - 10, name: 'set_card', gain: 0.6 },
    { at: TAG_HIT - 15, name: 'set_card', gain: 0.8 },
  ],

  /*
   * **HUD の登場**（v2。ユーザー 2026-10-01）: VS と DUEL START の間は HUD を出さず、着地のあとに組み上がる。
   * 出方（どこから・どう入るか）は climax の `drawHud.js::appearStyle`、進みのイージングはここ。
   * LP は 0 → 開始 LP へ数え上がる（10 刻み・ease_out）。満ちたら本来の値（`life` を言わない）。帯の終わりは全部 1
   */
  hud: ({ localMs: t, payload }) => {
    const lp = Number.isFinite(payload?.startLp) ? payload.startLp : 8000;
    const prog = (r, ease) => tween(t, r[0], r[1], ease);
    const plate = prog(H_PLATE, 'power3_in');
    const bar = prog(H_BAR, 'ease_out');
    const drop = prog(H_DROP, 'ease_out');
    const filling = t < H_FILL[1];
    const life = Math.round((lp * tween(t, H_FILL[0], H_FILL[1], 'power4_out')) / 10) * 10;
    // 顔の枠の着地・満ちた瞬間の小さな揺れ（位相は帯の経過）
    const slam = t >= H_PLATE[1] && t < H_PLATE[1] + 220 ? 0.6 * (1 - seg(t, H_PLATE[1], H_PLATE[1] + 220)) : 0;
    const full = t >= H_FILL[1] && t < H_FILL[1] + 200 ? 0.35 * (1 - seg(t, H_FILL[1], H_FILL[1] + 200)) : 0;
    const out = [0, 1].flatMap((player) => [
      { anchor: 'plate', player, appear: plate, shake: slam },
      { anchor: 'life', player, appear: bar, shake: full, ...(filling ? { life } : {}) },
      { anchor: 'hand', player, appear: drop },
    ]);
    const badgeLand = t >= H_BADGE[1] && t < H_BADGE[1] + 160 ? 1 - seg(t, H_BADGE[1], H_BADGE[1] + 160) : 0;
    out.push({ anchor: 'turnBadge', appear: prog(H_BADGE, 'power3_in'), pulse: badgeLand });
    return out;
  },

  layers: [{
    id: 'vs',
    label: '鋼の板の対面（VS → DUEL START）と先攻の札',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const { w: W, h: H } = canvasOf(ctx);
      const first = (ctx.payload.firstPlayer ?? ctx.payload.player) === 1 ? 1 : 0;
      const root = new THREE.Group();
      /*
       * three の透明物は一番近い祖先の group の renderOrder で並ぶので、root の直下に面を置くと group（板・帯）より先に描かれて隠れる。
       * 面は描く順ごとの group に入れる
       */
      const groups = {};
      const layer = (n) => { if (!groups[n]) groups[n] = add(root, new THREE.Group(), n); return groups[n]; };
      const cx = W / 2;
      const cy = H * 0.5;
      const nameOf = (p) => duelistName(ctx, p);
      const metal = (text, size, maxW, italic = false) => metalText(ctx, text, size, maxW, italic);
      const put = putIn;
      const tinted = (color, w, h, skew, opacity = 1) => tintedSkew(ctx, color, w, h, skew, opacity);

      // --- 暗幕（舞台を一様に少し落とす。HUD には掛からない。降りる間に戻す）
      const dim = add(layer(0), place(fill(ctx, '#050507', W, H), W / 2, H / 2), 0);

      // --- 板（P1 = 左、P2 = 右。同じ形で、中心を PW - SKEW 離すと切り口が噛み合う）
      const plates = [0, 1].map((p) => duelistPlate(ctx, root, p, { order: 4 }));
      const restX = [cx - (PW - SKEW) / 2, cx + (PW - SKEW) / 2];

      // --- 速度線（板の後ろに水平の硬い線）
      const rand = seeded(`${ctx.seed}:start`);
      const lines = Array.from({ length: LINES }, (_, i) => {
        const p = i % 2;
        const m = add(layer(3), fill(ctx, i % 3 === 0 ? '#ffffff' : '#c9d0da', 1, 1), 3);
        return { m, p, dy: (rand() - 0.5) * PH * 0.9, len: 260 + rand() * 360, lag: 40 + rand() * 260, w: 2 + rand() * 3 };
      });

      // --- 合わせ目（切り口の線）の閃光・火花・破片
      const seamAngle = Math.atan2(PH, -SKEW);   // 切り口の傾き（y 下向き）
      const seam = add(layer(9), fill(ctx, '#ffffff', 1, 1), 9);
      const sparkCols = ['#ffffff', '#ffd54a', '#ffb020', '#fff2b0'];
      const sparks = Array.from({ length: SPARKS }, (_, i) => {
        const m = add(layer(8), fill(ctx, sparkCols[i % sparkCols.length], 1, 1), 8);
        const side = i % 2 === 0 ? -1 : 1;
        return { m, a: (side < 0 ? Math.PI : 0) + (rand() - 0.5) * 1.6, along: (rand() - 0.5) * PH, len: 120 + rand() * 220, w: 3 + rand() * 4, speed: 900 + rand() * 1500 };
      });
      const shards = Array.from({ length: SHARDS }, (_, i) => {
        const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
        m.material.color?.set?.(i % 3 === 0 ? '#e6e9ee' : '#9aa1ad');
        add(layer(8), m, 8);
        return { m, a: rand() * Math.PI * 2, along: (rand() - 0.5) * PH, size: 12 + rand() * 22, stretch: 1.6 + rand() * 1.6, speed: 600 + rand() * 1000, spin: (rand() - 0.5) * 30 };
      });
      const flash = add(layer(12), place(fill(ctx, '#ffffff', W, H, { blend: 'add' }), W / 2, H / 2), 12);

      // --- VS
      const vs = metal('VS', 350, 2000, true);
      add(layer(10), vs.m, 10);

      // --- DUEL START の帯（暗い鋼の帯 ＋ 金の細い線 ＋ 銀の文字）
      const startBand = add(root, new THREE.Group(), 10);
      const BW = W + 200; const BH = 210;
      put(startBand, fill(ctx, '#0c0d11', BW, BH), 10);
      [-1, 1].forEach((s) => {
        const l = fill(ctx, '#c9a44c', BW, 4); l.position.set(0, s * (BH / 2 - 10), 0); put(startBand, l, 10.1);
        const r = fill(ctx, '#b01f2a', BW, 8); r.position.set(0, s * (BH / 2 - 2), 0); put(startBand, r, 10.1);
      });
      const startWord = metal('DUEL START', 170, W * 0.8, true);
      add(layer(11), startWord.m, 11);

      // --- 着地の輪（盤の上の楕円。硬い線の小片）
      const ringBars = Array.from({ length: RING_BARS }, () => add(layer(6), fill(ctx, '#ffffff', 1, 1), 6));

      // --- 先攻の札（先攻の色の平行四辺形に黒の縁、銀の文字）
      const tagColor = playerColor(ctx, first);
      const tagText = metal(`先攻  ${nameOf(first)}`, 80, W * 0.38);
      const TW = tagText.w + 170; const TH = 132; const TS = 44;
      const tag = add(root, new THREE.Group(), 10);
      put(tag, tinted('#050607', TW + 16, TH + 16, TS + 4), 10);
      put(tag, tinted(tagColor, TW, TH, TS), 10.1);
      put(tag, tinted('#0b0c10', TW - 36, TH - 30, TS - 8, 0.55), 10.2);
      tagText.m.scale.set(tagText.s, tagText.s, 1);
      put(tag, tagText.m, 10.3);
      const tagLine = add(layer(11), fill(ctx, '#ffffff', 1, 1), 11);
      const tagSide = first === 0 ? -1 : 1;
      const tagX = cx + tagSide * W * 0.24;
      const tagY = H * 0.74;

      return {
        root,
        update: ({ localMs: t }) => {
          const u = hs(t);
          fade(dim, 0.4 * (1 - tween(t, DESCEND, LAND, 'ease_in')));

          // --- 板: 加速して中央へ → 急停止 → 押し合い → 上下に裂けて外へ
          const inK = tween(t, SLIDE, HIT, 'power3_in');
          const push = t >= HIT + STOP && t < START_SLAM ? Math.sin(u / 23) * 3 * (1 - seg(u, HIT, START_AT)) + 2 : 0;
          const outK = tween(t, START_SLAM, PLATES_OUT, 'power2_in');
          plates.forEach(({ g, outer }, p) => {
            const from = restX[p] + outer * (W / 2 + PW);
            const x = from + (restX[p] - from) * inK - outer * push;
            const y = cy + (p === 0 ? -1 : 1) * outK * (H * 0.5 + PH);
            place(g, x, y, { rot: (p === 0 ? -1 : 1) * 0.06 * outK });
            fade(g, t >= SLIDE && t < PLATES_OUT ? 1 : 0);
          });

          // 速度線（滑り込みの間だけ。板の外側の後ろに付いて縮む）
          lines.forEach((l) => {
            const k = seg(t, SLIDE + 60, HIT + 40);
            const on = t >= SLIDE + 60 && t < HIT + 40;
            const outer = l.p === 0 ? -1 : 1;
            const from = restX[l.p] + outer * (W / 2 + PW);
            const px = from + (restX[l.p] - from) * inK;
            const len = l.len * (1 - k * 0.6);
            const x = px + outer * (PW / 2 + l.lag + len / 2);
            place(l.m, x, cy + l.dy, { sx: Math.max(0.001, len), sy: l.w });
            fade(l.m, on ? 0.9 : 0);
          });

          // 合わせ目の閃光（ぶつかった頭の 2 フレーム。切り口の形の硬い線）
          const seamOn = t >= HIT && t < HIT + 34;
          place(seam, cx, cy, { sx: Math.hypot(PH, SKEW) * 1.08, sy: 10, rot: seamAngle });
          fade(seam, seamOn ? 1 : 0);
          fade(flash, seamOn ? 0.12 : 0);

          // 火花と破片（止めの間はその場で止まる。合わせ目の線の上から左右へ）
          const since = Math.max(0, (u - HIT) / 1000);
          const ca = Math.cos(seamAngle); const sa = Math.sin(seamAngle);
          sparks.forEach((s) => {
            const k = Math.min(1, since / 0.42);
            const ox = cx + ca * s.along; const oy = cy + sa * s.along;
            const r = s.speed * since * (1 - 0.4 * k);
            const len = s.len * (1 - k);
            s.m.position.set(ox + Math.cos(s.a) * (r + len / 2), oy + Math.sin(s.a) * (r + len / 2), 0);
            s.m.rotation.z = s.a;
            s.m.scale.set(Math.max(0.001, len), s.w, 1);
            fade(s.m, t >= HIT && k < 1 ? 1 : 0);
          });
          shards.forEach((s) => {
            const k = Math.min(1, since / 0.6);
            const ox = cx + ca * s.along; const oy = cy + sa * s.along;
            const r = s.speed * since;
            s.m.position.set(ox + Math.cos(s.a) * r, oy + Math.sin(s.a) * r + 1400 * since * since, 0);
            s.m.rotation.z = s.a + s.spin * since;
            s.m.scale.set(s.size * s.stretch, s.size, 1);
            fade(s.m, t >= HIT && k < 1 ? 1 - k * k : 0);
          });

          // --- VS: 2.5 倍から叩きつけ → 小さく揺れて止まる → 細く潰れて消える
          const slam = tween(t, VS_AT - 30, VS_AT, 'power2_in');
          const settle = 1 + 0.06 * Math.sin(seg(t, VS_AT, VS_AT + 160) * Math.PI) * (t >= VS_AT ? 1 : 0);
          const vsSq = tween(t, START_AT, START_AT + 60, 'power2_in');
          const vsS = (2.5 - 1.5 * slam) * settle * vs.s;
          place(vs.m, cx + Math.sin(t / 17) * 2 * (1 - seg(t, VS_AT, VS_AT + 300)) * (t >= VS_AT ? 1 : 0), cy - 115, { sx: vsS * (1 + 0.2 * vsSq), sy: vsS * Math.max(0.001, 1 - vsSq) });
          fade(vs.m, t >= VS_AT - 30 && t < START_AT + 60 ? seg(t, VS_AT - 30, VS_AT - 10) : 0);

          // --- DUEL START: 叩きつけ（new_turn の TURN と同じ 2.6 倍 → 1 倍）→ カメラが動き出したら細く潰れて消える
          const sSlam = tween(t, START_SLAM - 30, START_SLAM, 'power2_in');
          const sSettle = t >= START_SLAM ? 1 + 0.05 * Math.sin(seg(t, START_SLAM, START_SLAM + 140) * Math.PI) : 1;
          const sSq = tween(t, DESCEND, DESCEND + 80, 'power2_in');
          const ws = (2.6 - 1.6 * sSlam) * sSettle * startWord.s;
          place(startWord.m, cx, cy, { sx: ws * (1 + 0.18 * sSq), sy: ws * Math.max(0.001, 1 - sSq) });
          const startShown = t >= START_SLAM - 30 && t < DESCEND + 80;
          fade(startWord.m, startShown ? seg(t, START_SLAM - 30, START_SLAM - 10) : 0);
          const bandOpen = tween(t, START_SLAM - 20, START_SLAM + 40, 'expo_out');
          place(startBand, cx, cy, { sx: 1, sy: Math.max(0.001, bandOpen * (1 - sSq)) });
          fade(startBand, t >= START_SLAM - 20 && t < DESCEND + 80 ? 1 : 0);

          // --- 着地の輪（2300 から 3 フレーム、広がりながら細くなる）
          const rk = seg(t, LAND, LAND + 100);
          const ringOn = t >= LAND && t < LAND + 100;
          const rr = 320 + 760 * tween(t, LAND, LAND + 100, 'expo_out');
          ringBars.forEach((m, i) => {
            const a = (i / RING_BARS) * Math.PI * 2;
            const x = cx + Math.cos(a) * rr;
            const y = cy + 30 + Math.sin(a) * rr * 0.5;
            const tangent = Math.atan2(Math.cos(a) * 0.5, -Math.sin(a));
            place(m, x, y, { sx: (2 * Math.PI * rr) / RING_BARS * 0.7, sy: Math.max(1, 7 * (1 - rk)), rot: tangent });
            fade(m, ringOn ? 0.95 : 0);
          });

          // --- 先攻の札: 先攻の側から叩きつけ → 見得 → 縦に潰れて外へ抜ける
          const tIn = tween(t, TAG_IN, TAG_HIT, 'power3_in');
          const tSq = tween(t, TAG_OUT, TAG_GONE, 'power2_in');
          const tx0 = tagX + tagSide * (W * 0.3 + TW);
          const tx = tx0 + (tagX - tx0) * tIn + tagSide * 260 * tSq;
          const bump = t >= TAG_HIT ? 1 + 0.06 * Math.sin(seg(t, TAG_HIT, TAG_HIT + 140) * Math.PI) : 1;
          place(tag, tx, tagY, { sx: bump * (1 + 0.15 * tSq), sy: bump * Math.max(0.001, 1 - tSq) });
          fade(tag, t >= TAG_IN && t < TAG_GONE ? 1 : 0);
          // 札の止まりの硬い線（2 フレーム）
          place(tagLine, tagX, tagY + TH / 2 + 14, { sx: TW * 1.1, sy: 4 });
          fade(tagLine, t >= TAG_HIT && t < TAG_HIT + 34 ? 0.9 : 0);
        },
      };
    },
  }],
}, 'start_effect.js');
